import { motion } from 'framer-motion';
import { Box, CheckCircle2, Home, Building2, Calendar, ShieldAlert } from 'lucide-react';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import SEO from '../components/ui/SEO';

export default function ContainerRental() {
  const solutions = [
    {
      title: "Affaldstyper vi håndterer",
      description: "Vi stiller containere op til alle typer opgaver og sørger for korrekt sortering og bortskaffelse.",
      icon: ShieldAlert,
      features: ["Nedrivningsopgaver", "Farligt affald", "Haveaffald", "Byggeaffald & murbrokker", "Stortblandingsaffald"]
    },
    {
      title: "Fleksible lejeaftaler",
      description: "Behovet for en container kan være forskelligt, og derfor tilbyder vi fleksible muligheder for leje.",
      icon: Calendar,
      features: ["Leje for en enkelt dag", "Leje i en weekend", "Længerevarende perioder", "Hurtig ombytning/tømning"]
    }
  ];

  return (
    <>
      <SEO 
        title="Lej en container" 
        description="Fleksibel containerudlejning til nedrivningsopgaver, farligt affald, haveaffald osv. Vi leverer, afhenter og bortskaffer affaldet korrekt med certifikat." 
      />

      {/* Hero */}
      <section className="pt-40 pb-20 bg-slate-50 text-slate-900 relative overflow-hidden">
        {/* Skewed Background Element */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#1d398e] hidden lg:block skew-x-[-12deg] translate-x-20 z-0" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center space-x-2 bg-blue-50 text-[#1d398e] px-4 py-2 rounded-full text-sm font-bold mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1d398e] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1d398e]"></span>
              </span>
              <span className="tracking-wide uppercase">Containerudlejning</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-[1.1] tracking-wide text-slate-900">
              Lej en <span className="text-[#1d398e]">container</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed max-w-2xl">
              Vi kommer og stiller en container til dit projekt, og henter den igen som aftalt. Vi skiller os af med affaldet på forsvarlig og godkendt vis.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions */}
      <Section>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-xl md:text-4xl font-bold text-blue-900 mb-4">Professionel containerløsning</h2>
          <p className="text-gray-600">
            Uanset om du står overfor en nedrivning, havearbejde eller skal af med farligt affald, har vi den rette container og det nødvendige certifikat til at hjælpe dig.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 flex flex-col"
            >
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center text-blue-900 mb-6">
                <solution.icon className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4">{solution.title}</h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                {solution.description}
              </p>
              <ul className="space-y-3 mb-10 flex-grow">
                {solution.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-center text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-3 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button to="/kontakt" variant={index === 1 ? 'primary' : 'secondary'} className="w-full">
                Få et tilbud
              </Button>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Image & Text Split */}
      <Section light>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
            <img 
              src="https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/3f933ef5-2341-4c5f-900c-41539c75ec1f.jpg" 
              alt="Vores container og biler" 
              className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
            />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-xl md:text-3xl font-bold text-blue-900 mb-6">Fleksibel leje & certificeret bortskaffelse</h2>
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                Behovet for en container kan være forskelligt, og derfor tilbyder vi fleksible muligheder for leje. Du kan leje den for både en dag, en weekend eller i en længere periode, afhængigt af hvad du har brug for.
              </p>
              <p>
                Når containeren er fyldt op, skal affaldet bortskaffes korrekt, og her har vi certifikat til at udføre dette, så du trygt kan overlade den opgave til os.
              </p>
              <p className="font-semibold text-slate-900">
                Vi stiller containeren præcis som aftalt, og når du er færdig, kommer vi og henter den igen og skiller os af med affaldet på miljøgodkendt vis.
              </p>
            </div>
            <div className="mt-10">
              <Button to="/kontakt">Kontakt os for at høre om priser</Button>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
